import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  MarkAllReadResponseSchema,
  NotificationSchema,
  NotificationsListResponseSchema,
  UnreadCountResponseSchema,
  type Notification,
  type NotificationsListResponse,
} from '@repo/api-client/dtos';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { getJSON, postJSON } from '../internal/http';

type QueryEnableOptions = { enabled?: boolean };

/**
 * Paginated notification inbox. Returns a flat array (we don't yet
 * need infinite scrolling on the client) plus the next-page cursor.
 */
export function useNotifications(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () =>
      getJSON(http, '/notifications', NotificationsListResponseSchema),
    enabled,
  });
  return {
    notifications: (query.data?.items ?? []) as Notification[],
    nextCursor: query.data?.nextCursor ?? null,
    loading: query.isLoading,
    refetch: query.refetch,
    error: query.error,
  };
}

/**
 * Cheap unread count for the bell badge.
 *
 * Polls every 60s while the app is in the foreground. React Query
 * parks the request when the tab/screen is hidden
 * (`refetchIntervalInBackground: false` — the default, made explicit
 * here so the intent is reviewable) so backgrounded mobile / tabbed-
 * away web don't drain the battery or flood the logs.
 *
 * Why polling and not SSE / WebSocket: at this app's scale (single
 * notification kind, low write rate, no in-app real-time UX), a
 * 60s poll costs one ~1ms server hit per active user per minute
 * and one in-flight HTTP request — both negligible. SSE / WebSocket
 * would buy us seconds of badge latency at the cost of:
 *
 *   - A long-lived connection per active client (Coolify's Traefik
 *     terminates TLS for each one, doubling FD usage for a tab
 *     that does almost nothing).
 *   - A new failure mode (proxy idle-timeout drops, mid-flight
 *     `notification.created` events silently lost, harder to test
 *     deterministically).
 *   - Server-side fanout plumbing (Redis pub/sub or BullMQ stream)
 *     that doesn't exist yet.
 *
 * The denylist in `RequestContextInterceptor` already silences the
 * `http.request.completed` debug log for this endpoint, so the
 * remaining noise from the poll itself is invisible. If we ever
 * grow real-time notification kinds (typing indicators, presence,
 * collab cursors) the answer flips — at that point lift this hook
 * to read from a shared SSE stream and delete the poll. Until then,
 * polling is the right amount of complexity.
 */
export function useNotificationsUnreadCount(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () =>
      getJSON(http, '/notifications/unread-count', UnreadCountResponseSchema),
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
  return {
    count: query.data?.count ?? 0,
    loading: query.isLoading,
    error: query.error,
  };
}

export function useMarkNotificationRead() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      postJSON(http, `/notifications/${id}/read`, {}, NotificationSchema),
    onSuccess: (notification) => {
      queryClient.setQueryData<NotificationsListResponse>(
        queryKeys.notifications.list(),
        (cache) =>
          cache
            ? {
                ...cache,
                items: cache.items.map((n) => (n.id === notification.id ? notification : n)),
              }
            : cache,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
    },
  });
  return {
    markRead: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

export function useMarkAllNotificationsRead() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      postJSON(http, '/notifications/read-all', {}, MarkAllReadResponseSchema),
    onSuccess: () => {
      // Optimistically zero out the unread count and patch the cached
      // list so every row picks up `readAt`.
      queryClient.setQueryData(
        queryKeys.notifications.unreadCount(),
        { count: 0 },
      );
      const now = new Date().toISOString();
      queryClient.setQueryData<NotificationsListResponse>(
        queryKeys.notifications.list(),
        (cache) =>
          cache
            ? {
                ...cache,
                items: cache.items.map((n) => (n.readAt ? n : { ...n, readAt: now })),
              }
            : cache,
      );
    },
  });
  return {
    markAllRead: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
